import React, { useState, useEffect } from "react";
import { NavLink as RouterNavLink, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Collapse,
  Container,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { useAuth0 } from "@auth0/auth0-react";
import history from "../utils/history";
import { fetchOrganisationsForUser } from "../utils/apiService";
import { useOrg, pickInitialOrgId } from "../utils/orgContext";
import NotificationBell from "./NotificationBell";

const orgIdOf = (org) => Number(org?.orgId || org?.id);

const OrgSwitcher = ({ getAccessTokenSilently, onOrgChange }) => {
  const { orgId, selectOrg, markReady } = useOrg();
  const [orgs, setOrgs] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        let token = null;
        try { token = await getAccessTokenSilently(); } catch (e) {}
        const data = await fetchOrganisationsForUser(token).catch(() => null);
        const list = data?.completeOrgList || data?.values || (Array.isArray(data) ? data : []);
        setOrgs(list);
        const initialId = pickInitialOrgId(list, orgId);
        const initialIndex = Math.max(0, list.findIndex((o) => orgIdOf(o) === initialId));
        setSelectedIndex(initialIndex);
        selectOrg(initialId);
        if (list.length > 0) onOrgChange(list[initialIndex]);
      } catch (e) {}
      finally {
        setLoading(false);
        markReady();
      }
    };
    load();
    // orgId is only read for the first pick; re-running on every switch would refetch the list
  }, [getAccessTokenSilently]);

  const handleChange = (e) => {
    const idx = Number(e.target.value);
    setSelectedIndex(idx);
    if (orgs[idx]) {
      selectOrg(orgIdOf(orgs[idx]));
      onOrgChange(orgs[idx]);
    }
  };

  return (
    <select
      value={selectedIndex}
      onChange={handleChange}
      disabled={loading || orgs.length === 0}
      style={{
        fontSize: '0.82rem',
        fontWeight: 600,
        color: 'var(--color-text)',
        background: 'var(--color-bg-subtle)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '4px 8px',
        cursor: loading || orgs.length === 0 ? 'default' : 'pointer',
        outline: 'none',
        maxWidth: '160px',
      }}
    >
      {loading ? (
        <option>Loading...</option>
      ) : orgs.length === 0 ? (
        <option>No teams</option>
      ) : (
        orgs.map((org, i) => (
          <option key={i} value={i}>
            {org.orgName || org.name || `Team ${org.orgId || org.id}`}
          </option>
        ))
      )}
    </select>
  );
};

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const {
    user,
    isAuthenticated,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  const toggle = () => setIsOpen(!isOpen);

  const logoutWithRedirect = () =>
    logout({
        logoutParams: {
          returnTo: window.location.origin,
        }
    });

  useEffect(() => {
    const unlisten = history.listen(() => {
      setIsOpen(false);
    });
    return () => {
      unlisten();
    };
  }, []);

  return (
    <div className="nav-container">
      <Navbar color="white" light expand="md" container={false} fixed="top" style={{ borderBottom: '1px solid #e9ecef', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', padding: '0 0' }}>
        <Container>
          <NavbarBrand className="logo">
            <img
              src="/Logo-Only.png"
              alt="BillsMax Logo"
              height="60"
            />
          </NavbarBrand>
          <NavbarToggler onClick={toggle} />
          <Collapse isOpen={isOpen} navbar>
            <Nav className="mr-auto align-items-center" navbar>
              <NavItem>
                <NavLink
                  tag={RouterNavLink}
                  to="/"
                  exact
                  activeClassName="router-link-exact-active"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={RouterNavLink}
                  to="/suppliers/1"
                  activeClassName="router-link-exact-active"
                  onClick={() => setIsOpen(false)}
                >
                  Suppliers
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={RouterNavLink}
                  to="/receipts"
                  exact
                  activeClassName="router-link-exact-active"
                  onClick={() => setIsOpen(false)}
                >
                  Receipts
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={RouterNavLink}
                  to={selectedOrg ? `/team/${selectedOrg.orgId || selectedOrg.id}` : '/teams'}
                  activeClassName="router-link-exact-active"
                  onClick={() => setIsOpen(false)}
                >
                  Team
                </NavLink>
              </NavItem>
            </Nav>
            <Nav className="d-none d-md-flex align-items-center" navbar>
              {!isAuthenticated && (
                <NavItem>
                  <Button
                    id="qsLoginBtn"
                    color="primary"
                    className="btn-margin"
                    onClick={() => loginWithRedirect()}
                  >
                    Login
                  </Button>
                </NavItem>
              )}
              {isAuthenticated && (
                <NavItem style={{ marginRight: '0.5rem' }}>
                  <OrgSwitcher getAccessTokenSilently={getAccessTokenSilently} onOrgChange={setSelectedOrg} />
                </NavItem>
              )}
              {isAuthenticated && (
                <NavItem style={{ display: 'flex', alignItems: 'center' }}>
                  <NotificationBell getAccessTokenSilently={getAccessTokenSilently} />
                </NavItem>
              )}
              {isAuthenticated && (
                <NavItem style={{ display: 'flex', alignItems: 'center' }}>
                  <Link
                    to="/settings"
                    title="Settings"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '44px', height: '44px', borderRadius: '50%',
                      color: '#4a5568', textDecoration: 'none',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                  </Link>
                </NavItem>
              )}
              {isAuthenticated && (
                <UncontrolledDropdown nav inNavbar>
                  <DropdownToggle nav caret id="profileDropDown">
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="nav-user-profile rounded-circle"
                      width="36"
                    />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem header>{user.name}</DropdownItem>
                    <DropdownItem
                      tag={RouterNavLink}
                      to="/settings"
                      activeClassName="router-link-exact-active"
                    >
                      <FontAwesomeIcon icon="user" className="mr-3" /> Settings
                    </DropdownItem>
                    <DropdownItem
                      id="qsLogoutBtn"
                      onClick={() => logoutWithRedirect()}
                    >
                      <FontAwesomeIcon icon="power-off" className="mr-3" /> Log
                      out
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </Nav>
            {!isAuthenticated && (
              <Nav className="d-md-none" navbar>
                <NavItem>
                  <Button
                    id="qsLoginBtn"
                    color="primary"
                    block
                    onClick={() => loginWithRedirect({})}
                  >
                    Login
                  </Button>
                </NavItem>
              </Nav>
            )}
            {isAuthenticated && (
              <Nav
                className="d-md-none justify-content-between mt-3"
                navbar
                style={{ minHeight: 170 }}
              >
                <NavItem>
                  <span className="user-info">
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="nav-user-profile d-inline-block rounded-circle mr-3"
                      width="50"
                    />
                    <h6 className="d-inline-block">{user.name}</h6>
                  </span>
                </NavItem>
                <NavItem>
                  <FontAwesomeIcon icon="user" className="mr-3" />
                  <RouterNavLink
                    to="/settings"
                    activeClassName="router-link-exact-active"
                  >
                    Settings
                  </RouterNavLink>
                </NavItem>
                <NavItem>
                  <FontAwesomeIcon icon="power-off" className="mr-3" />
                  <RouterNavLink
                    to="#"
                    id="qsLogoutBtn"
                    onClick={() => logoutWithRedirect()}
                  >
                    Log out
                  </RouterNavLink>
                </NavItem>
              </Nav>
            )}
          </Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

export default NavBar;
